import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import {
  CheckIcon,
  ChevronRightIcon,
  CopyIcon,
  Loader2,
  LogOutIcon,
  Share2Icon,
  ShieldCheckIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { NoteBubble } from '@/components/coaching/coach-notes'
import { coachInviteLink, toastCoachError } from '@/components/coaching/coach-utils'
import { RolePill } from '@/components/coaching/role-pill'
import {
  SettingsGroup,
  SettingsRow,
  SettingsSection,
  Toggle,
  rowId,
} from '@/components/settings/primitives'
import { UserAvatar } from '@/components/user-avatar'
import { useGoals } from '@/hooks/use-goals'

type Scope = 'all' | 'picked'
export type Perms = {
  scope: Scope
  goalIds?: Id<'nodes'>[]
  canSeeHabits: boolean
  canSeeCheckins: boolean
}
type Coach = NonNullable<FunctionReturnType<typeof api.coaching.myCoach>>
type PastCoach = FunctionReturnType<typeof api.coaching.pastCoaches>[number]

export const DEFAULT_PERMS: Perms = { scope: 'all', canSeeHabits: true, canSeeCheckins: true }

export function ScopeEditor({ value, onChange }: { value: Perms; onChange: (p: Perms) => void }) {
  const { t } = useTranslation()
  const goals = (useGoals() ?? []).filter((g) => !g.isInbox && g.status !== 'done')
  const picked = new Set(value.goalIds ?? [])
  return (
    <>
      <div className="flex gap-1.5 px-4 py-3">
        {(['all', 'picked'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() =>
              onChange({
                ...value,
                scope: s,
                goalIds: s === 'picked' ? (value.goalIds ?? []) : undefined,
              })
            }
            className={cn(
              'cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
              value.scope === s
                ? 'border-primary/40 bg-primary/10 text-primary-text'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(s === 'all' ? 'coaching.settings.scopeAll' : 'coaching.settings.scopePicked')}
          </button>
        ))}
        {value.scope === 'all' && (
          <span className="self-center pl-1 text-xs text-muted-foreground">
            {t('coaching.settings.scopeAllDesc')}
          </span>
        )}
      </div>
      {value.scope === 'picked' && (
        <div className="max-h-56 overflow-y-auto px-2 pb-2">
          {goals.length === 0 && (
            <p className="px-2 py-2 text-xs text-muted-foreground">
              {t('coaching.settings.noGoals')}
            </p>
          )}
          {goals.map((g) => {
            const on = picked.has(g._id)
            return (
              <button
                key={g._id}
                type="button"
                onClick={() => {
                  const next = new Set(picked)
                  if (on) next.delete(g._id)
                  else next.add(g._id)
                  onChange({ ...value, goalIds: [...next] })
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm hover:bg-muted/50"
              >
                <span
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded border',
                    on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                  )}
                >
                  {on && <CheckIcon className="size-3" strokeWidth={3} />}
                </span>
                <span className="truncate">{g.title}</span>
              </button>
            )
          })}
        </div>
      )}
    </>
  )
}

export function PermRows({ value, onChange }: { value: Perms; onChange: (p: Perms) => void }) {
  const { t } = useTranslation()
  return (
    <>
      <SettingsRow
        id={rowId('coaching.settings.habits')}
        label={t('coaching.settings.habits')}
        description={t('coaching.settings.habitsDesc')}
      >
        <Toggle
          checked={value.canSeeHabits}
          onChange={(canSeeHabits) => onChange({ ...value, canSeeHabits })}
          label={t('coaching.settings.habits')}
        />
      </SettingsRow>
      <SettingsRow
        id={rowId('coaching.settings.checkins')}
        label={t('coaching.settings.checkins')}
        description={t('coaching.settings.checkinsDesc')}
      >
        <Toggle
          checked={value.canSeeCheckins}
          onChange={(canSeeCheckins) => onChange({ ...value, canSeeCheckins })}
          label={t('coaching.settings.checkins')}
        />
      </SettingsRow>
    </>
  )
}

function InviteBuilder() {
  const { t, i18n } = useTranslation()
  const invite = useQuery(api.coaching.myInvite)
  const create = useMutation(api.coaching.createInvite)
  const revoke = useMutation(api.coaching.revokeInvite).withOptimisticUpdate((store) =>
    store.setQuery(api.coaching.myInvite, {}, null),
  )
  const [perms, setPerms] = useState<Perms>(DEFAULT_PERMS)
  const [busy, setBusy] = useState(false)
  const pickedNone = perms.scope === 'picked' && !perms.goalIds?.length

  async function handleCreate() {
    setBusy(true)
    try {
      await create(perms)
    } catch (err) {
      toastCoachError(err, t)
    } finally {
      setBusy(false)
    }
  }

  if (invite) {
    const link = coachInviteLink(invite.token)
    const copy = () =>
      navigator.clipboard
        .writeText(link)
        .then(() => toast.success(t('coaching.settings.copied')))
        .catch(() => {})
    return (
      <SettingsGroup label={t('coaching.settings.inviteTitle')}>
        <div className="space-y-3 px-4 py-3.5">
          <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2">
            <span className="min-w-0 flex-1 truncate font-mono text-xs">
              {link.replace(/^https?:\/\//, '')}
            </span>
            <button
              type="button"
              onClick={copy}
              className="flex cursor-pointer items-center gap-1 text-xs font-semibold text-primary-text"
            >
              <CopyIcon className="size-3.5" />
              {t('coaching.settings.copy')}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {typeof navigator.share === 'function' && (
              <button
                type="button"
                onClick={() =>
                  navigator
                    .share({ title: t('coaching.settings.shareText'), url: link })
                    .catch(() => {})
                }
                className="flex h-9 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground hover:bg-primary/90"
              >
                <Share2Icon className="size-4" />
                {t('coaching.settings.share')}
              </button>
            )}
            <button
              type="button"
              onClick={() => void revoke({}).catch(() => {})}
              className="h-9 cursor-pointer rounded-full border px-4 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {t('coaching.settings.revoke')}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('coaching.settings.linkHint', {
              date: new Date(invite.expiresAt).toLocaleDateString(i18n.language, {
                month: 'short',
                day: 'numeric',
              }),
            })}
          </p>
        </div>
      </SettingsGroup>
    )
  }

  return (
    <SettingsGroup label={t('coaching.settings.inviteTitle')}>
      <p className="px-4 pt-3.5 text-sm text-muted-foreground">
        {t('coaching.settings.inviteDesc')}
      </p>
      <ScopeEditor value={perms} onChange={setPerms} />
      <PermRows value={perms} onChange={setPerms} />
      <div className="px-4 py-3.5">
        <button
          type="button"
          disabled={busy || invite === undefined || pickedNone}
          onClick={handleCreate}
          className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
        >
          {busy && <Loader2 className="size-4 animate-spin" />}
          {t('coaching.settings.create')}
        </button>
      </div>
    </SettingsGroup>
  )
}

function LinkedCoach({ coach }: { coach: Coach }) {
  const { t, i18n } = useTranslation()
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [editingScope, setEditingScope] = useState(false)
  const update = useMutation(api.coaching.updateLink).withOptimisticUpdate((store, args) => {
    const cur = store.getQuery(api.coaching.myCoach, {})
    if (cur) store.setQuery(api.coaching.myCoach, {}, { ...cur, ...args })
  })
  const end = useMutation(api.coaching.endCoaching).withOptimisticUpdate((store) =>
    store.setQuery(api.coaching.myCoach, {}, null),
  )
  const perms: Perms = {
    scope: coach.scope,
    goalIds: coach.goalIds,
    canSeeHabits: coach.canSeeHabits,
    canSeeCheckins: coach.canSeeCheckins,
  }
  const save = (p: Perms) => {
    if (p.scope === 'picked' && !p.goalIds?.length) return
    void update(p).catch((err) => toastCoachError(err, t))
  }

  return (
    <>
      <SettingsGroup label={t('coaching.settings.yourCoach')}>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <UserAvatar
            image={coach.coach.image}
            name={coach.coach.name}
            size={40}
            className="size-10 shrink-0 rounded-full ring-2 ring-coach ring-offset-2 ring-offset-card"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-sm font-semibold">{coach.coach.name}</p>
              <RolePill role="coach" />
            </div>
            <p className="text-xs text-coach-text">
              {t('coaching.settings.since', {
                date: new Date(coach.since).toLocaleDateString(i18n.language, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }),
              })}
            </p>
          </div>
        </div>
      </SettingsGroup>
      <SettingsGroup label={t('coaching.settings.canSee', { name: coach.coach.name })}>
        <SettingsRow
          label={
            coach.scope === 'all'
              ? t('coaching.settings.scopeAll')
              : t('coaching.settings.pickedCount', { count: coach.goalIds?.length ?? 0 })
          }
          description={coach.scope === 'all' ? t('coaching.settings.scopeAllDesc') : undefined}
          onClick={() => setEditingScope((v) => !v)}
        >
          <span className="text-xs font-semibold text-primary-text">
            {editingScope ? t('coaching.done') : t('coaching.edit')}
          </span>
        </SettingsRow>
        {editingScope && <ScopeEditor value={perms} onChange={save} />}
        <PermRows value={perms} onChange={save} />
        <div className="flex items-start gap-2.5 px-4 py-3 text-xs text-muted-foreground">
          <ShieldCheckIcon className="mt-0.5 size-3.5 shrink-0" />
          <span>{t('coaching.settings.privacy')}</span>
        </div>
      </SettingsGroup>
      <SettingsGroup danger>
        <SettingsRow
          id={rowId('coaching.settings.end')}
          icon={<LogOutIcon className="h-4 w-4" />}
          label={t('coaching.settings.end')}
          onClick={() => setConfirmEnd(true)}
          destructive
        />
      </SettingsGroup>
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('coaching.settings.endTitle', { name: coach.coach.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('coaching.settings.endDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setConfirmEnd(false)
                void end({ linkId: coach.linkId }).catch((err) => toastCoachError(err, t))
              }}
            >
              {t('coaching.settings.end')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function useSpan(c: PastCoach) {
  const { i18n } = useTranslation()
  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, { month: 'short', year: 'numeric' })
  return c.endedAt ? `${fmt(c.startedAt)} – ${fmt(c.endedAt)}` : fmt(c.startedAt)
}

function PastNotesSheet({
  past,
  open,
  onClose,
}: {
  past: PastCoach
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const notes = useQuery(api.coaching.pastNotes, { linkId: past.linkId })
  const span = useSpan(past)
  const threads = new Map<string, { title: string; notes: NonNullable<typeof notes> }>()
  for (const n of notes ?? []) {
    const key = n.goalId ?? ''
    const thread = threads.get(key) ?? {
      title: n.goalTitle ?? t('coaching.settings.pastGeneral'),
      notes: [],
    }
    thread.notes.push(n)
    threads.set(key, thread)
  }
  const ordered = [...threads].sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : 0))
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <SheetTitle className="text-base">
            {t('coaching.goal.notesWith', { name: past.coach.name })}
          </SheetTitle>
          <SheetDescription>{span}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {notes === undefined ? (
            <div className="h-16 animate-pulse rounded-xl bg-muted/40" />
          ) : (
            ordered.map(([key, thread]) => (
              <section key={key}>
                <h3 className="mb-2 truncate text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {thread.title}
                </h3>
                <ul className="space-y-2.5">
                  {thread.notes.map((n) => (
                    <NoteBubble
                      key={n._id}
                      note={n}
                      viewerIsCoach={false}
                      author={n.mine ? t('coaching.notes.you') : past.coach.name}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}
          <p className="text-xs text-muted-foreground">{t('coaching.goal.endedHint')}</p>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function PastCoachRow({ past, onOpen }: { past: PastCoach; onOpen: () => void }) {
  const { t } = useTranslation()
  const span = useSpan(past)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 active:bg-muted"
    >
      <UserAvatar
        image={past.coach.image}
        name={past.coach.name}
        size={32}
        className="size-8 shrink-0 rounded-full opacity-80 ring-2 ring-coach/40 ring-offset-2 ring-offset-card"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-sm font-medium">{past.coach.name}</p>
          <RolePill role="coach" />
        </div>
        <p className="text-xs text-muted-foreground">
          {span} · {t('coaching.settings.pastNoteCount', { count: past.noteCount })}
        </p>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
    </button>
  )
}

function PastNotes() {
  const { t } = useTranslation()
  const past = useQuery(api.coaching.pastCoaches)
  const [selected, setSelected] = useState<PastCoach | null>(null)
  const [open, setOpen] = useState(false)
  if (!past?.length) return null
  return (
    <>
      <SettingsGroup label={t('coaching.settings.pastNotes')}>
        {past.map((p) => (
          <PastCoachRow
            key={p.linkId}
            past={p}
            onOpen={() => {
              setSelected(p)
              setOpen(true)
            }}
          />
        ))}
      </SettingsGroup>
      {selected && <PastNotesSheet past={selected} open={open} onClose={() => setOpen(false)} />}
    </>
  )
}

export function MyCoachSection() {
  const { t } = useTranslation()
  const coach = useQuery(api.coaching.myCoach)
  return (
    <SettingsSection id="mycoach" title={t('settings.sec.mycoach')}>
      {coach === undefined ? (
        <div className="h-24 animate-pulse rounded-xl border bg-card" />
      ) : coach ? (
        <LinkedCoach coach={coach} />
      ) : (
        <InviteBuilder />
      )}
      <PastNotes />
    </SettingsSection>
  )
}
